import store from '../store';
import logger from '../clients/logger';
import alertHandler from '../errorHandling/alertHandler';
import authManager from '../auth/authManager';
import buildInfo from '../buildInfo';
import { cloneDeep } from 'lodash';

const defaultHeaders = {
  'Content-Type': 'application/json',
  'X-Powered-By': `Authress Restrict UI; ${buildInfo.version.releaseDate}.${buildInfo.version.buildCommit}.${buildInfo.version.buildNumber}`
};

const errorMessages = new Set([
  'Failed to fetch', // Chrome
  'NetworkError when attempting to fetch resource.', // Firefox
  'The Internet connection appears to be offline.', // Safari 16
  'Network request failed', // `cross-fetch`
  'fetch failed', // Undici (Node.js)
  'Load failed', // iOS Fetch failed to respond - https://stackoverflow.com/questions/71280168/javascript-typeerror-load-failed-error-when-calling-fetch-on-ios
  '<HTML DOCUMENT></HTML>' // Handle some HTML error page responses as well, or sometimes CDN is having problems, if the response includes an HTML Document, then for sure there was an issue
]);

function isNetworkError(error) {
  return error.message === 'Network Error' || error.code === 'ERR_NETWORK' || !error.status || error.status >= 500
    || typeof error.message === 'string' && errorMessages.has(error.message)
    || typeof error.data === 'string' && errorMessages.has(error.data);
}

async function retryExecutor(func) {
  let lastError = null;
  for (let iteration = 0; iteration < 5; iteration++) {
    try {
      const result = await func();
      return result;
    } catch (error) {
      error.retryCount = iteration;
      lastError = error;
      if (isNetworkError(error)) {
        lastError.isNetworkError = true;
        // Transient connectivity problems and CDN blips last seconds, so back off far enough to actually span one, with jitter to avoid retrying in lockstep across concurrent requests
        const backoffInMillis = 250 * 2 ** iteration;
        await new Promise(resolve => setTimeout(resolve, backoffInMillis + Math.random() * backoffInMillis / 2));
        continue;
      }
      throw error;
    }
  }
  throw lastError;
}

function logSuccess(logObject, response) {
  logger.debug(Object.assign({ title: 'Platform HTTP Request' }, logObject, { status: response?.status }));
}

async function logError(logObject = { url: null, requestHeaders: null, requestData: null, method: null },
  requestError = { message: null, url: null, status: null, data: null, headers: null, errorTransformationError: null, code: null }) {
  const requestUrl = requestError.url;
  // The error may or may not have been rewritten by or handlers, so assume the error status could be anywhere
  const errorStatus = requestError.status;
  if (errorStatus === 401) {
    logger.track({ title: `Authress UI: Found 401 request, it should be impossible to get these, so this is an error: ${requestUrl}`, errorRaw: requestError, logObject });
    const resolvedToken = await authManager.getToken({ timeoutInMillis: 0 }).catch(e => e);
    authManager.trackUnauthorizedRequest({ errorStatus, requestUrl, logObject, error: requestError, resolvedToken });
  }

  const reportedError = cloneDeep(requestError);
  if (requestError.message === 'Network Error' || requestError.code === 'NetworkError' || requestError.code === 'ECONNABORTED') {
    reportedError.originalCode = requestError?.code;
    reportedError.url = requestUrl;
    reportedError.code = 'NetworkError';
    store.commit('trackHttpError', 'NetworkError');
  }
  if (errorStatus === 429) {
    store.commit('trackHttpError', 'RateLimitError');
  }
  if (store.state.cache.errorTracking.NetworkError?.count > 1) {
    alertHandler.renderAlert('Network connectivity issue', 'Authress has detected a network connectivity issue with your device. This can affect the usability of the Management Portal. If you continue to experience issues, please reach out via a Support Ticket.', 'danger', 5000);
  }

  const notFound = errorStatus === 404;
  const newError = {
    online: navigator.onLine,
    method: logObject.method,
    url: logObject.url,
    data: reportedError.data,
    status: errorStatus,
    headers: reportedError.headers,
    message: reportedError.message,
    code: reportedError.code,
    stack: reportedError.stack
  };

  const level = notFound ? 'debug' : 'warn';
  logger[level](Object.assign({ title: 'Platform HTTP Error' }, logObject, { error: newError }), false);
}

class PlatformClient {
  get(url, headers) {
    return retryExecutor(() => {
      return this.handleFetchWrapper('GET', url, null, headers);
    });
  }

  delete(url, headers) {
    return retryExecutor(() => {
      return this.handleFetchWrapper('DELETE', url, null, headers);
    });
  }

  post(url, data, headers) {
    return retryExecutor(() => {
      return this.handleFetchWrapper('POST', url, data, headers);
    });
  }

  put(url, data, headers) {
    return retryExecutor(() => {
      return this.handleFetchWrapper('PUT', url, data, headers);
    });
  }

  patch(url, data, headers) {
    return retryExecutor(() => {
      return this.handleFetchWrapper('PATCH', url, data, headers);
    });
  }

  async handleFetchWrapper(method, urlObject, requestData, requestHeaders) {
    const url = urlObject.toString();

    try {
      const token = await authManager.getToken();
      if (!token) {
        logger.track({ title: 'We should start requiring a valid session token here. It does not make sense to call the API without the token, We should validate when a user is on a screen, if they have not used the UI in a long time, have no token, and the last time the navigation guard was run was a long time ago, then we would have run the guard and it would have failed, so we should force the user to reload the page, anyway.' });
        // eslint-disable-next-line no-throw-literal
        throw { status: 401, message: 'Unauthorized: No Authorization header was specified in the Authress UI platformClient.' };
      }
      const allHeaders = Object.assign({}, defaultHeaders, requestHeaders, { Authorization: `Bearer ${token}` });
      const request = { method, headers: allHeaders };
      if (requestData) {
        request.body = JSON.stringify(requestData);
      }
      const response = await fetch(url, request);

      if (!response.ok) {
        throw response;
      }

      let responseBody = {};
      let bodyTransformationError = null;
      try {
        responseBody = await response?.text();
      } catch (bodyParseError) {
        bodyTransformationError = bodyParseError;
      }

      try {
        responseBody = JSON.parse(responseBody || '{}');
      } catch (bodyParseError) {
        // We don't care why it couldn't parse, maybe it isn't JSON at all
      }

      const fullResponse = {
        url,
        headers: response.headers,
        status: response.status,
        data: responseBody,
        bodyTransformationError
      };

      logSuccess({ url: url.toString(), headers: requestHeaders, method }, fullResponse);
      return fullResponse;
    } catch (fetchError) {
      let resolvedErrorMessage = fetchError;
      let errorTransformationError = null;
      try {
        resolvedErrorMessage = await resolvedErrorMessage.text?.();
      } catch (parseError) {
        errorTransformationError = parseError;
      }

      try {
        resolvedErrorMessage = JSON.parse(resolvedErrorMessage || '{}');
      } catch (bodyParseError) {
        // We don't care why it couldn't parse, maybe it isn't JSON at all
      }

      const extensionErrorId = fetchError?.stack?.match(/chrome-extension:[/][/](\w+)[/]/)?.[1];
      if (extensionErrorId) {
        logger.info({ title: `Fetch fallback also due to a chrome extension - ${method} - ${url}`, method, url, data: requestData, headers: requestHeaders, fetchError, resolvedErrorMessage, extensionErrorId, errorTransformationError });
        alertHandler.createAlert('Network connection failed', `An extension you have installed in your browser has a bug that is blocking Standup & Prosper network requests. Obviously I don't have access to your extensions so all I have is an id. Please disable the browser extension with id: ${extensionErrorId}`, 'danger');
        throw Error.create({ extensionErrorId }, 'BROWSER_EXTENSION_ERROR');
      }

      const httpError = {
        message: fetchError.message,
        code: fetchError.code,
        url: url,
        status: fetchError.status,
        data: resolvedErrorMessage,
        headers: fetchError.headers,
        errorTransformationError,
        stack: fetchError.stack
      };
      await logError({ url: url.toString(), requestHeaders: requestHeaders || {}, requestData: requestData, method }, httpError);
      throw httpError;
    }
  }
}
export default new PlatformClient();
